import type { RewardSettings } from '@/models/rewardSettings'
import type { UserStats } from '@/models/userStats'
import type { WarStats } from '@/models/warStats'
import { TornApi } from './tornApiService'

async function getWarReport(apiKey: string) {
  const tornApi = new TornApi(apiKey)
  const factionWarReport = await tornApi.getWarReport()

  if (!factionWarReport?.factionId || !factionWarReport?.rankedWar) {
    return
  }

  const lastWarReport = factionWarReport.rankedWar.factions.find(
    (x) => x.id === factionWarReport.factionId,
  )

  if (!lastWarReport) {
    return
  }

  const opponent = factionWarReport.rankedWar.factions.find(
    (x) => x.id !== factionWarReport.factionId,
  )

  const currentUnixTimeInSeconds = Math.floor(Date.now() / 1000)
  const start = factionWarReport.rankedWar.start
  const end = factionWarReport.rankedWar.end || currentUnixTimeInSeconds

  const playerChainReports = await tornApi.getChainReports(start, end)

  const factionId = lastWarReport.id
  const opponentId = opponent?.id

  let playerDefends: { [playerId: number]: number } = {}
  let playerRevives: { [playerId: number]: number } = {}

  if (factionId && opponentId) {
    playerDefends = await tornApi.getLosses(factionId, opponentId, start, end)
    playerRevives = await tornApi.getRevives(factionId, opponentId, start, end)
  }

  const totalAttacks = lastWarReport.attacks
  const totalRespect = lastWarReport.score
  let bonusRespect = 0
  let totalAssists = 0
  let totalChainBuilds = 0
  for (const pid in playerChainReports) {
    bonusRespect += playerChainReports[pid]?.bonus ?? 0
    totalAssists += playerChainReports[pid]?.assists ?? 0
    totalChainBuilds += playerChainReports[pid]?.chainBuilds ?? 0
  }

  let totalRevives = 0
  for (const pid in playerRevives) {
    totalRevives += playerRevives[pid] ?? 0
  }

  return {
    id: factionWarReport.rankedWar.id,
    opponent: opponent?.name,
    totalAttacks,
    totalRespect,
    totalAssists,
    totalRevives,
    totalChainBuilds,
    bonusRespect,
    playerChainReports,
    lastWarReport,
    playerDefends,
    playerRevives,
  }
}

export async function calculateRewards(settings: RewardSettings): Promise<
  | {
      warStats: WarStats
      userStats: UserStats[]
    }
  | undefined
> {
  const warReport = await getWarReport(settings.apiKey)

  if (!warReport) {
    return undefined
  }

  const minMedOuts = settings.minMedOuts ?? 0

  // Only count med outs for users meeting the minimum
  // Calculate totalMedOuts as the sum of filtered med outs
  let totalMedOuts = 0
  for (const pid in warReport.playerDefends) {
    const count = warReport.playerDefends[pid] ?? 0
    if (count >= minMedOuts) {
      totalMedOuts += count
    }
  }

  const calculatedRespect = Math.max(warReport.totalRespect - warReport.bonusRespect, 0)
  const totalAttacks =
    (warReport.totalAttacks ?? 0) +
    (settings.includeChainBuilders ? (warReport.totalChainBuilds ?? 0) : 0)
  const rewardPerRespect =
    settings.payoutType == 'perRespect' ? settings.attackRewards / (calculatedRespect || 1) : 0
  const rewardPerAttack =
    settings.payoutType == 'perAttack' ? settings.attackRewards / (totalAttacks || 1) : 0

  const warStats: WarStats = {
    rankedWarId: warReport.id,
    factionName: warReport.lastWarReport.name,
    opponentName: warReport.opponent ?? '',
    totalAttacks,
    totalRespect: warReport.totalRespect,
    totalAssists: warReport.totalAssists,
    totalMedOuts,
    totalRevives: warReport.totalRevives,
    totalChainBuilds: warReport.totalChainBuilds,
    rewardPerRespect,
    rewardPerAttack,
    rewardPerAssist: settings.assistRewards / (warReport.totalAssists || 1),
    rewardPerMedOut: settings.medOutRewards / (totalMedOuts || 1),
    rewardPerRevive: settings.reviveRewards / (warReport.totalRevives || 1),
    rewardPerChainBuild: settings.chainBuilderRewards / (warReport.totalChainBuilds || 1),
  }

  const userStats = warReport.lastWarReport.members
    .map((user) => {
      let playerRespect = user.score
      const playerBonus = warReport.playerChainReports[user.id]?.bonus ?? 0
      const playerAssists = warReport.playerChainReports[user.id]?.assists ?? 0
      const playerChainBuilds = warReport.playerChainReports[user.id]?.chainBuilds ?? 0
      const playerMedOutsRaw = warReport.playerDefends[user.id] ?? 0
      // Only count med outs for users meeting the minimum
      const playerMedOuts = playerMedOutsRaw >= minMedOuts ? playerMedOutsRaw : 0
      const playerRevives = warReport.playerRevives[user.id] ?? 0

      if (settings.payoutType === 'perRespect' && settings.ignoreChainBonus) {
        playerRespect -= playerBonus
      }

      // Helper to blank 0/NaN
      const blank = (val: number | undefined | null) => (!val || isNaN(val) ? 0 : val)

      const rewardAttackRespect = blank(
        settings.payoutType === 'perAttack'
          ? (user.attacks + (settings.includeChainBuilders ? playerChainBuilds : 0)) *
              (warStats.rewardPerAttack ?? 0)
          : playerRespect * (warStats.rewardPerRespect ?? 0),
      )

      const rewardAssists = blank(playerAssists * warStats.rewardPerAssist)
      const rewardMedOuts = blank(playerMedOuts * warStats.rewardPerMedOut)
      const rewardRevives = blank(playerRevives * warStats.rewardPerRevive)
      const rewardChainBuilds = blank(playerChainBuilds * warStats.rewardPerChainBuild)
      const totalRewards = blank(
        rewardAttackRespect + rewardAssists + rewardMedOuts + rewardRevives + rewardChainBuilds,
      )

      return {
        id: user.id,
        name: user.name,
        attacks: blank(user.attacks),
        respect: blank(user.score),
        bonusRespect: blank(playerBonus),
        assists: blank(playerAssists),
        medOuts: blank(playerMedOutsRaw),
        revives: blank(playerRevives),
        chainBuilds: blank(playerChainBuilds),
        rewardAttackRespect,
        rewardAssists,
        rewardMedOuts,
        rewardRevives,
        rewardChainBuilds,
        totalRewards,
      }
    })
    // Filter out users with no rewards (0 or NaN for all reward fields)
    .filter((u) =>
      [
        u.rewardAttackRespect,
        u.rewardAssists,
        u.rewardMedOuts,
        u.rewardRevives,
        u.totalRewards,
      ].some((val) => val),
    )

  return {
    warStats,
    userStats,
  }
}
